import { Component } from '@angular/core';
import { Subject, switchMap, takeUntil, filter as rxFilter, lastValueFrom } from 'rxjs';
import { DataAction } from 'src/app/base/enum/data-action.enum';
import { DialogReturnData } from 'src/app/base/models/dialog-return-data.model';
import { WHInvBookkeepingService } from '../../services/w-h-inv-bookkeeping.service';
import { SimpleSearchComponent } from 'src/app/base/components/abstract/simple/simple-search.component';
import { Option } from 'src/app/base/models/option.model';
import { StockSummaryService } from '../../services/stock-summary.service';
import { CommonService } from '../../services/common.service';
import { MenuItem } from 'primeng/api';
import { ValidationService } from 'src/app/base/services/validation.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { WHInvBookkeepingComponent } from '../w-h-inv-bookkeeping/w-h-inv-bookkeeping.component';
import { Validators, AbstractControl, ValidationErrors, ValidatorFn, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-w-h-inv-bookkepping',
  templateUrl: './w-h-inv-bookkeeping-search.component.html',
  styleUrls: ['./w-h-inv-bookkeeping-search.component.scss'],
  providers: [DialogService]
})
export class WHInvBookkeepingSearchComponent extends SimpleSearchComponent {
  constructor(
    private wHInvBookkeepingService: WHInvBookkeepingService,
    private stockSummaryService: StockSummaryService,
    private commonService: CommonService,
    protected override validationService: ValidationService,
    protected override dialogService: DialogService
  ) {
    super();
  }

  override rowDataKeyName: string = 'rowIndex';

  override rowDataDeleteHintFieldName: string = 'uuid';

  override componentClassName: string = 'WHInvBookkeepingSearchComponent';

  // 查詢結果回傳的 queryId
  queryId: string = '';

  // 頁籤資料
  searchTabs: MenuItem[] = [];
  
  // 目前作用中的頁籤
  activeSearchTab!: MenuItem;

  // 是否顯示 batchId dialog
  displayBatchDialog = false;

  // 目前 batchId
  batchId = null;

  // 明細 dialog ref，用於控制動態彈窗
  detailDialogRef?: DynamicDialogRef;

  // OU autocomplete清單
  ouSuggestions: Option[] = [];

  // Item autocomplete清單
  itemSuggestions: Option[] = [];
  
  // 處理狀態下拉選單
  processStatusOptions: Option[] = [];

  private readonly ouSubject$ = new Subject<string | undefined>();
  private readonly itemSubject$ = new Subject<string | undefined>();

  /**
   * 切換 Tab 時更新目前 active tab
   */
  onSearchTabActiveItemChange(activeItem: MenuItem): void {
    this.activeSearchTab = activeItem;
    this.submitted = false;

    this.formGroup.markAsUntouched();
    this.formGroup.markAsPristine();

    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.get(key)?.markAsUntouched();
      this.formGroup.get(key)?.markAsPristine();
      this.formGroup.get(key)?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
  }

  /**
   * 初始化表單
   * Search tab:
   * - OU、Item 必填
   * - Begin Month / End Month 預設帶入最近三個月區間
   * - 月份格式驗證 yyyyMM
   * - 月份區間驗證 Begin <= End
   *
   * Search Result tab:
   * - 處理狀態、Batch ID、起訖日期
   * - End Date 需符合日期區間驗證
   */
  override initFormGroup(): void {
    // 當月
    const now = new Date();
    const endMonth = now.getFullYear().toString() + ('0' + (now.getMonth() + 1)).slice(-2);

    // 當月 - 2 個月
    const beginDate = new Date();
    beginDate.setDate(1);
    beginDate.setMonth(beginDate.getMonth() - 2);
    const beginMonth = beginDate.getFullYear().toString() + ('0' + (beginDate.getMonth() + 1)).slice(-2);

    this.formGroup = this.formBuilder.nonNullable.group(
      {
        // Search
        uuid: this.dynamicDialogConfig?.data?.uuid ?? '',
        ouFormControlName: [null, Validators.required],
        itemFormControlName: [null, Validators.required],
        beginMonthFormControlName: [
          beginMonth,
          [this.monthFormatValidator(true)]
        ],
        endMonthFormControlName: [
          endMonth,
          [this.monthFormatValidator(true)]
        ],

        // Search Result
        processStatusFormControlName: [null],
        batchIdFormControlName: [null],
        createDateFormControlName: new Date(),
        endDateFormControlName: new Date(),
      },
      {
        // FormGroup 層級驗證：月份區間檢查
        validators: [
          this.monthRangeValidator(
            'beginMonthFormControlName',
            'endMonthFormControlName'
          )
        ]
      }
    );
  }

  /**
   * 初始化表格欄位
   * Search Result tab 查詢結果使用
   */
  override initTableColumns(): void {
    // 先建立頁籤
    this.buildSearchTabs();

    this.tableColumns = [
      {
        header: 'field.processing-status',
        field: 'processStatusDisplay',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.batch-id',
        field: 'batchId',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.created-by',
        field: 'createdBy',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.create-date',
        field: 'createdDate',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.end-date',
        field: 'updatedDate',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
    ];
  }

  /**
   * 初始化表單資料
   * 若是從 dialog 或其他地方帶入 uuid，則可直接執行查詢
   */
  override async initFormData(): Promise<any> {
    this.loadingMaskService.show();
    const dialogData = this.dynamicDialogConfig?.data;

    if (dialogData) {
      this.formGroup.patchValue({
        uuid: dialogData.uuid ?? '',
        ouFormControlName: dialogData.ou ?? null,
        itemFormControlName: dialogData.itemNumber
          ? {
            value: dialogData.itemNumber,
            label: dialogData.itemNumber,
            id: dialogData.inventoryItemId,
          }
          : null
      });
    }
    // 有 uuid 才自動查詢
    if (this.dynamicDialogConfig?.data.uuid) {
      const formValue = this.formGroup.getRawValue();

      const queryCondition = {
        ...this.dynamicDialogConfig.data,
        beginMonth: formValue.beginMonthFormControlName,
        endMonth: formValue.endMonthFormControlName
      };

      await this.doQuery(queryCondition);
    }
    return true;
  }

  /**
   * 初始化額外明細功能選單
   */
  override initExtraDetailMenu(): void {
    this.detailMenu = [];
  }

  /**
   * 查詢 OU autocomplete 建議資料
   */
  searchOuSuggestions(event: { query: any }): void {
    if (!this.ouSubject$.observed) {
      this.ouSubject$
        .pipe(
          switchMap((keyword) => this.stockSummaryService.getOrgs(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => (this.ouSuggestions = data));
    }
    this.ouSubject$.next(event.query);
  }

  /**
   * 查詢 Item autocomplete 建議資料
   */
  searchItemSuggestions(event: { query: any }): void {
    if (!this.itemSubject$.observed) {
      this.itemSubject$
        .pipe(
          rxFilter(Boolean),
          switchMap((keyword: string) => this.commonService.getItems(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => (this.itemSuggestions = data),
          error: () => (this.itemSuggestions = []),
        });
    }
    this.itemSubject$.next(event.query);
  }

  /**
   * 查詢（Search tab）
   * 流程：
   * 1. 先做表單驗證
   * 2. 組查詢條件
   * 3. 呼叫 API
   * 4. 取得 queryId / batchId
   * 5. 若有 batchId 則顯示 dialog
   */
  async submitQuery(): Promise<void> {
    this.submitted = true;

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formValue = this.formGroup.getRawValue();
    const ouObj = formValue.ouFormControlName;
    const itemObj = formValue.itemFormControlName;

    // 組 API 查詢條件
    const queryCondition = {
      uuid: formValue.uuid ?? '',
      ouId: ouObj?.value ?? null,
      inventoryItemId: itemObj?.id ?? null,
      itemNumber: itemObj?.label ?? '',
      privFlag: 'Y',
      beginMonth: formValue.beginMonthFormControlName,
      endMonth: formValue.endMonthFormControlName
    };

    await this.doQuery(queryCondition);
  }

  /**
   * Search 頁籤重置按鈕
   * - 還原初始表單值
   * - 清空查詢結果相關欄位
   */
  override searchReset(): void {
    this.formGroup.reset();
    this.formGroup.patchValue(this.originalFormRawValue);
    this.submitted = false;
    this.displayBatchDialog = false;
    this.batchId = null;
    this.queryId = '';
    this.formGroup.patchValue({
      ouFormControlName: null,
      itemFormControlName: null,
    });
  }

  /**
   * Search Result 頁籤查詢按鈕
   * 依條件查詢 batch 執行結果清單
   */
  async queryResult(): Promise<void> {
    this.submitted = true;

    const formValue = this.formGroup.getRawValue();
    // 組查詢條件
    const queryCondition = {
      processStatus: formValue.processStatusFormControlName,
      batchId: formValue.batchIdFormControlName? formValue.batchIdFormControlName: null,
      createDate: this.formatDate(formValue.createDateFormControlName),
      endDate: this.formatDate(formValue.endDateFormControlName)
    };

    try {
      this.loadingMaskService.show();

      const res = await lastValueFrom(
        this.wHInvBookkeepingService
          .getWHInvBookkeepingResult(queryCondition)
          .pipe(takeUntil(this._destroying$))
      );
      // 若 API 回傳 failure，清空資料
      if (res?.failure) {
        this.detailDatas = [];
        return;
      }
      // 取出 detail 陣列，若無資料則給空陣列
      const detailRows = Array.isArray(res?.responseData) ? res.responseData : [];
      // 補上 rowIndex 供表格 key 使用
      this.detailDatas = detailRows.map((row: any, index: number) => ({
        ...row,
        rowIndex: index,
        processStatusDisplay: this.getProcessStatusDisplay(row)
      }));

    } catch (e) {
      console.error(e);
      this.detailDatas = [];
    } finally {
      this.loadingMaskService.hide();
    }
  }

  /**
   * Search Result 頁籤重置按鈕
   * - 清空查詢條件
   * - 日期重設為今天
   * - 清空表格資料
   */
  resultReset(): void {
    this.formGroup.patchValue({
      processStatusFormControlName: null,
      batchIdFormControlName: null,
      createDateFormControlName: new Date(),
      endDateFormControlName: new Date()
    });

    this.detailDatas = [];
  }

  /**
   * 開啟 batchId detail 明細視窗
   * 將 uuid 傳入 detail component
   */
  openBatchIdDetail(rowData: any): void {
    const request = {
      uuid: rowData.uuid
    };

    this.detailDialogRef = this.dialogService.open(WHInvBookkeepingComponent, {
      header: 'W.H INV.Bookkeeping Batch Id : ' + rowData.batchId,
      width: '80vw',
      height: 'auto',
      modal: false,
      closable: true,
      draggable: true,
      dismissableMask: false,
      data: request
    });
  }

  /**
   * 日期格式轉換：Date -> yyyy-MM-dd
   */
  private formatDate(date: any): string | null {
    if (!date) {
      return null;
    }

    const d = new Date(date);
    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);

    return `${year}-${month}-${day}`;
  }

  /**
   * 建立處理狀態下拉選單
   * P: executing
   * F: finish
   * E: error
   * I: request queueing
   */
  private buildProcessStatusOptions(): void {
    this.processStatusOptions = [
      {
        label: this.translateService.instant('processing-status.executing'),
        value: 'P',
        id: ''
      },
      {
        label: this.translateService.instant('processing-status.finish'),
        value: 'F',
        id: ''
      },
      {
        label: this.translateService.instant('processing-status.error'),
        value: 'E',
        id: ''
      },
      {
        label: this.translateService.instant('processing-status.request_queueing'),
        value: 'I',
        id: ''
      }
    ];
  }

  /**
   * 多語系顯示 processStatus
   */
  private getProcessStatusDisplay(row: any): string {
    const lang = this.translateService.currentLang;

    if (lang === 'en-us') return row.processStatusUs ?? '';
    if (lang === 'zh-tw') return row.processStatusTw ?? '';
    if (lang === 'zh-cn') return row.processStatusCn ?? '';

    return row.processStatusTw ?? row.processStatusCn ?? row.processStatusUs ?? row.processStatus;

  }

  /**
   * 建立搜尋頁籤
   * 語系切換後可重建 label
   * 同時保留目前 active tab
   */
  private buildSearchTabs(): void {
    const currentActiveId = this.activeSearchTab?.id;

    this.searchTabs = [
      {
        id: this.parseToId('search'),
        label: this.translateService.instant('label.search'),
        automationId: 'label.search',
      },
      {
        id: this.parseToId('searchResult'),
        label: this.translateService.instant('label.search-result'),
        automationId: 'label.search-result',
      },
    ];

    this.activeSearchTab =
      this.searchTabs.find(tab => tab.id === currentActiveId) ?? this.searchTabs[0];
  }


  /**
   * 鍵盤事件處理
   * Enter / Space 時避免頁面捲動或誤觸發
   */
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // 防止滾動頁面
    }
  }

  /**
   * 初始化靜態選項
   */
  override initStaticOptions(): void {
    this.buildProcessStatusOptions();
  }

  /**
   * 語系切換後重建靜態文字
   * - 重新建立 Tab label
   * - 重新建立處理狀態文字
   * - 重新顯示處理狀態文字
   */
  override afterLangChange(): void {
    this.buildSearchTabs();
    this.buildProcessStatusOptions();
    this.detailDatas = (this.detailDatas || []).map((row: any) => ({
      ...row,
      processStatusDisplay: this.getProcessStatusDisplay(row)
    }));
  }

  /** 月份格式：yyyyMM，且 MM 必須為 01~12 */
  private readonly MONTH_PATTERN = /^\d{4}(0[1-9]|1[0-2])$/;

  /**
   * 月份格式檢核
   * 規則：
   * - required = true 時，必填
   * - 格式必須為 yyyyMM
   * - 月份範圍必須為 01~12
   */
  private monthFormatValidator(required: boolean = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.toString().trim();
      // 空值處理
      if (!value) {
        return required ? { required: true } : null;
      }
      // 格式不符
      if (!this.MONTH_PATTERN.test(value)) {
        return { monthFormat: true };
      }

      return null;
    };
  }

  /**
   * 月份區間檢核
   * 規則：
   * - Begin / End 皆有值時才檢核
   * - 若欄位格式本身錯誤，交由欄位 validator 處理
   * - Begin Month 必須小於等於 End Month
   */
  private monthRangeValidator(beginControlName: string, endControlName: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const formGroup = group as FormGroup;
      const beginValue = formGroup.get(beginControlName)?.value?.toString().trim();
      const endValue = formGroup.get(endControlName)?.value?.toString().trim();
      // 任一空值不檢核
      if (!beginValue || !endValue) {
        return null;
      }

      // 格式不正確時，先交給各自欄位 validator 顯示格式錯誤
      if (!this.MONTH_PATTERN.test(beginValue) || !this.MONTH_PATTERN.test(endValue)) {
        return null;
      }

      // Begin Month 必須小於等於 End Month
      if (beginValue > endValue) {
        return { monthRangeInvalid: true };
      }

      return null;
    };
  }

  /**
   * 呼叫 API（取得 batchId）
   */
  override async doQuery(data: any): Promise<boolean> {
    try {
      this.loadingMaskService.show();

      const res = await lastValueFrom(
        this.wHInvBookkeepingService
          .getWHInvBookkeeping(data)
          .pipe(takeUntil(this._destroying$))
      );

      // 若 API 回傳 failure，清空資料
      if (res?.failure) {
        this.systemMessageService.error(res?.exceptionMsg);
        this.detailDatas = [];
        return false;
      }
      // 取得 queryId / batchId
      this.queryId = res?.queryId ?? '';
      this.batchId = res?.responseData?.batchId ?? null;

      // 控制 dialog
      this.displayBatchDialog = !!this.batchId;

      return true;
    } catch (error) {
      console.error(error);
      this.queryId = '';
      this.batchId = null;
      this.displayBatchDialog = false;
      return false;
    } finally {
      this.loadingMaskService.hide();
    }
  }

  /**
   * batch dialog 確認
   * - 帶入 batchId
   * - 切 tab
   * - 自動查詢 result
   */
  async onBatchDialogConfirm(): Promise<void> {
    // 關閉 dialog
    this.displayBatchDialog = false;

    // 將 batchId 自動帶入查詢條件
    this.formGroup.patchValue({
      batchIdFormControlName: this.batchId
    });

    // 切換到 查詢結果 tab
    const searchResultTab = this.searchTabs.find(
      tab => tab.id === this.parseToId('searchResult')
    );

    if (searchResultTab) {
      this.activeSearchTab = searchResultTab;
    }

    // 自動查詢結果
    await this.queryResult();
  }

  //以下未實作
  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override async doRowActionDelete(): Promise<boolean> {
    return true;
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    return;
  }

  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }

  override setExtraDetailMenuStatus(): void {
    return;
  }

  override initDialogConfig(dataAction: DataAction): void {
    return;
  }

  override initExtraRowActionMenu(): void {
    return;
  }

  override setExtraRowActionMenuStatus(): void {
    return;
  }

  override doExtraDetailDialogClose(returnData: DialogReturnData): void {
    return;
  }

  override afterDetailTabActiveItemChange(): void {
    return;
  }

  override afterFormDataLoadSuccess(): void {
    return;
  }

  override afterFormDataLoadFail(): void {
    return;
  }

  override initFormControlDisabled(): void {
    return;
  }

  override async doMasterMenuSubmit(): Promise<boolean> {
    return true;
  }

  override async doMasterMenuDelete(): Promise<boolean> {
    return true;
  }

  override initExtraMasterMenu(): void {
    return;
  }

  override setExtraMasterMenuStatus(): void {
    return;
  }

}
